import { useCallback } from "react"
import { css } from "@emotion/css"
import { useQuery } from "react-query"
import { http } from "api/http"

import { DataEnvelop } from "@dashboards/helpers/api"
import { Card, CardTitle } from "@dashboards/components/Card"
import { makeGroups } from "@dashboards/components/lib/groups"
import { ColorScale, HeatGrid } from "@dashboards/components/html/HeatGrid"
import { Loading } from "@dashboards/components/Loading"
import { Empty } from "@dashboards/components/Empty"
import { customSGDFormat, compactShort } from "@dashboards/helpers/formatters"

import { scaleQuantize } from "@visx/scale"

import useFilters from "../filters/hook"
import {
  NetGrossTotalFilter,
  useNetGrossTotalFilterState,
  KindOfAmount,
} from "../filters/NetGrossTotal"

const styles = {
  card: css`
    min-height: 450px;
  `,
  header: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
  scaleContainer: css`
    display: flex;
    justify-content: flex-end;
    padding: var(--spacing-l, 20px) 0 0 0;
  `,
  container: css`
    flex: 1;
    padding: var(--spacing-l, 20px) 0 0 0;
  `,
}

const colors = [
  "rgba(0, 177, 164, 0.5)",
  "rgba(51, 193, 182, 0.7)",
  "#00b1a4",
  "#09a59a",
  "#069388",
]

export default function MarketMediaMixHeatmap() {
  const { getSearchParams } = useFilters({ filters: ["startDate", "endDate"] })
  const { data, status } = useQuery(
    ["reporting/regional/spend-by-market-media-mix", getSearchParams()],
    fetchData,
  )
  const items = data?.data ?? []
  const isEmpty = status === "success" && items.length === 0

  const ngt = useNetGrossTotalFilterState()
  const kindOfAmount = ngt.value
  const amountKey = amountKeyMap[kindOfAmount]

  const groups = getGroups(items, amountKey)
  const max = groups?.max ?? 0

  const getColor = useCallback(
    (val: number) => {
      const scale = scaleQuantize({
        domain: [0, max],
        range: colors,
      })
      return scale(val) || colors[0]
    },
    [max],
  )

  return (
    <Card className={styles.card}>
      <div className={styles.header}>
        <CardTitle>Market Media Mix Heatmap</CardTitle>
        <NetGrossTotalFilter {...ngt} />
      </div>
      {status === "loading" ? (
        <Loading />
      ) : isEmpty ? (
        <Empty />
      ) : status === "error" ? (
        <Empty title="something went wrong" />
      ) : (
        <>
          <div className={styles.scaleContainer}>
            <ColorScale
              colors={colors}
              minLabel="0"
              maxLabel={compactShort(max)}
            />
          </div>
          <div className={styles.container}>
            <HeatGrid
              keys={groups?.keys ?? []}
              rows={groups?.rows ?? []}
              chartLabel={""}
              getColor={getColor}
              formatValue={customSGDFormat}
            />
          </div>
        </>
      )}
    </Card>
  )
}

type Item = {
  marketName: string
  mediaCode: string
  totalSpend: number
  netSpend: number
  grossSpend: number
}

type ItemAmount = keyof Omit<Item, "marketName" | "mediaCode">
const amountKeyMap: Record<KindOfAmount, ItemAmount> = {
  net: "netSpend",
  gross: "grossSpend",
  total: "totalSpend",
}

type Response = DataEnvelop<Item>

function getGroups(items: Item[], amountKey: ItemAmount) {
  const data = items

  const groups = makeGroups(
    data,
    (x) => x.mediaCode ?? "Other",
    (x) => x.marketName,
    (x) => x[amountKey],
  )

  return groups
}

const fetchData = async (key: string, params?: string) => {
  const searchParams = new URLSearchParams(params)
  const res: Response = await http(key, { searchParams, timeout: false }).json()
  return res
}
